import { useState } from "react"
import { usePage } from "@inertiajs/react"
import { track } from "@/lib/analytics"
import type { Render, SharedProps, SocialConnection, Script } from "@/types"
import { csrfToken } from "@/lib/csrf"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import PlatformCaptionTabs from "@/components/dashboard/posts/platform-caption-tabs"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { DateTimePicker } from "@/components/ui/date-time-picker"
import {
  INITIAL_TIKTOK_STATE,
  isTiktokStateValid,
  type TiktokPostState,
} from "@/lib/tiktok-post-state"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
type Platform = "instagram" | "facebook" | "tiktok" | "youtube"

const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
}

const COMING_SOON_PLATFORMS: Set<Platform> = new Set()

export default function PublishDialog({
  videoId,
  render,
  connections,
  activeScript,
  onPublished,
  size = "lg",
  variant = "charcoal",
  label = "Post to socials",
  listingLat,
  listingLng,
}: {
  videoId: string
  render: Render
  connections: SocialConnection[]
  activeScript?: Script
  onPublished?: () => void
  size?: "default" | "sm" | "lg"
  variant?: "charcoal" | "red"
  label?: string
  listingLat?: number | string | null
  listingLng?: number | string | null
}) {
  const { currentUser } = usePage<SharedProps>().props
  const [open, setOpen] = useState(false)
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(new Set())
  const [captions, setCaptions] = useState<Record<Platform, string>>(() => {
    const initial: Record<string, string> = {}
    const content = activeScript?.content as Record<string, string> | undefined
    for (const p of ["instagram", "facebook", "tiktok", "youtube"] as Platform[]) {
      initial[p] = content?.[`caption_${p}`] || ""
    }
    return initial as Record<Platform, string>
  })
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<Date | undefined>(undefined)
  const [publishing, setPublishing] = useState(false)
  const [generatingCaption, setGeneratingCaption] = useState<Record<string, boolean>>({})
  const [facebookPageId, setFacebookPageId] = useState<string>("")
  const [facebookPostType, setFacebookPostType] = useState<"reel" | "video">("reel")
  const [tiktokState, setTiktokState] = useState<TiktokPostState>(INITIAL_TIKTOK_STATE)
  const [locationId, setLocationId] = useState<string | null>(null)
  const [locationName, setLocationName] = useState<string | null>(null)
  const [locationMeta, setLocationMeta] = useState<{ address?: string; verified?: boolean; picture_url?: string; hours?: string } | null>(null)
  const [shareToFeed, setShareToFeed] = useState(true)

  // Find the Facebook connection's managed pages
  const fbConnection = connections.find(
    (c) => c.platform === "facebook" && c.status === "active",
  )
  const fbPages = fbConnection?.managed_pages || []

  // Track platforms that already have a post for this render (informational only)
  const existingPlatforms = new Set(
    (render.social_posts || []).map((p) => p.platform),
  )

  function togglePlatform(platform: Platform) {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev)
      if (next.has(platform)) {
        next.delete(platform)
      } else {
        next.add(platform)
      }
      return next
    })
  }

  async function handlePublish() {
    if (selectedPlatforms.size === 0) return

    setPublishing(true)
    try {
      const captionsObj: Record<string, string> = {}
      for (const p of selectedPlatforms) {
        captionsObj[p] = captions[p]
      }

      const response = await fetch(
        `/dashboard/videos/${videoId}/renders/${render.id}/social_posts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
          },
          body: JSON.stringify({
            platforms: Array.from(selectedPlatforms),
            captions: captionsObj,
            scheduled_at: scheduleEnabled && scheduledAt ? scheduledAt.toISOString() : null,
            facebook_page_id: selectedPlatforms.has("facebook") && facebookPageId ? facebookPageId : null,
            facebook_post_type: selectedPlatforms.has("facebook") ? facebookPostType : null,
            share_to_feed: selectedPlatforms.has("instagram") ? shareToFeed : null,
            ...(selectedPlatforms.has("tiktok")
              ? {
                  tiktok_privacy: tiktokState.privacy,
                  tiktok_disable_comment: !tiktokState.allowComments,
                  tiktok_disable_duet: !tiktokState.allowDuet,
                  tiktok_disable_stitch: !tiktokState.allowStitch,
                  tiktok_brand_content_toggle: tiktokState.brandedContent,
                  tiktok_brand_organic_toggle: tiktokState.yourBrand,
                }
              : {}),
            location_id: locationId,
            location_name: locationName,
            location_address: locationMeta?.address || null,
            location_verified: locationMeta?.verified || null,
            location_picture_url: locationMeta?.picture_url || null,
            location_hours: locationMeta?.hours || null,
          }),
        },
      )

      if (response.ok) {
        track("social_post_published", {
          platforms: Array.from(selectedPlatforms),
          scheduled: scheduleEnabled,
        })
        toast.success(
          scheduleEnabled
            ? "Posts scheduled successfully"
            : selectedPlatforms.has("tiktok")
              ? "Publishing to social media. TikTok may take a few minutes to process your video."
              : "Publishing to social media...",
        )
        setOpen(false)
        setSelectedPlatforms(new Set())
        onPublished?.()
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to create posts")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setPublishing(false)
    }
  }

  async function handleGenerateCaption(platform: string) {
    setGeneratingCaption((prev) => ({ ...prev, [platform]: true }))
    try {
      const response = await fetch(
        `/dashboard/videos/${videoId}/renders/${render.id}/social_posts/generate_caption`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
          },
          body: JSON.stringify({ platform }),
        },
      )

      if (response.ok) {
        const data = await response.json()
        setCaptions((prev) => ({ ...prev, [platform]: data.caption }))
        track("caption_generated", { platform, video_id: videoId })
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to generate caption")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setGeneratingCaption((prev) => ({ ...prev, [platform]: false }))
    }
  }

  const hasConnections = connections.length > 0

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size={size}
          className={`font-semibold text-white ${
            variant === "red"
              ? "bg-red shadow-sm hover:bg-deep-red"
              : "bg-charcoal hover:bg-charcoal/90"
          }`}
        >
          <svg
            className="mr-1.5 size-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z"
            />
          </svg>
          {label}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Publish to social media</DialogTitle>
          <DialogDescription>
            Choose which platforms to post to and customise your captions.
          </DialogDescription>
        </DialogHeader>

        {!hasConnections ? (
          <NoConnectionsCallout />
        ) : (<>

        {/* Platform selection */}
        <div className="space-y-3 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
            Platforms
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(["instagram", "facebook", "tiktok", "youtube"] as Platform[]).map(
              (platform) => {
                const comingSoon = COMING_SOON_PLATFORMS.has(platform)
                const connection = connections.find(
                  (c) => c.platform === platform && c.status === "active",
                )
                const alreadyPosted = existingPlatforms.has(platform)
                const isSelected = selectedPlatforms.has(platform)
                const disabled = comingSoon || !connection

                return (
                  <button
                    key={platform}
                    type="button"
                    onClick={() => !disabled && togglePlatform(platform)}
                    disabled={disabled}
                    className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-all ${
                      disabled
                        ? "cursor-not-allowed border-stone/40 bg-stone/5 text-warm-grey/40"
                        : isSelected
                          ? "border-red/50 bg-red/5 text-charcoal"
                          : "border-stone/60 text-warm-grey hover:border-stone"
                    }`}
                  >
                    {comingSoon ? (
                      <svg className="size-4 text-warm-grey/40" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                      </svg>
                    ) : (
                      <span
                        className={`flex size-4 items-center justify-center rounded border text-[10px] ${
                          isSelected
                            ? "border-red bg-red text-white"
                            : "border-stone/70"
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="size-2.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={3}
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m4.5 12.75 6 6 9-13.5"
                            />
                          </svg>
                        )}
                      </span>
                    )}
                    <span className="font-medium">
                      {PLATFORM_LABELS[platform]}
                    </span>
                    {comingSoon && (
                      <span className="ml-auto text-[10px] text-warm-grey/50">
                        Coming soon
                      </span>
                    )}
                    {!comingSoon && alreadyPosted && (
                      <span className="ml-auto text-[10px] text-warm-grey/50">
                        Posted
                      </span>
                    )}
                    {!comingSoon && !connection && !alreadyPosted && (
                      <span className="ml-auto text-[10px] text-warm-grey/50">
                        Not connected
                      </span>
                    )}
                  </button>
                )
              },
            )}
          </div>
        </div>

        {/* Captions & platform options */}
        {selectedPlatforms.size > 0 && (
          <PlatformCaptionTabs
            platforms={Array.from(selectedPlatforms)}
            captions={captions}
            onCaptionChange={(platform, value) =>
              setCaptions((prev) => ({ ...prev, [platform]: value }))
            }
            onGenerateCaption={handleGenerateCaption}
            generatingCaption={generatingCaption}
            facebookPages={fbPages}
            facebookPageId={facebookPageId}
            onFacebookPageChange={setFacebookPageId}
            facebookPostType={facebookPostType}
            onFacebookPostTypeChange={setFacebookPostType}
            shareToFeed={shareToFeed}
            onShareToFeedChange={setShareToFeed}
            locationId={locationId}
            locationName={locationName}
            locationMeta={locationMeta}
            onLocationChange={(id, name, meta) => {
              setLocationId(id)
              setLocationName(name)
              setLocationMeta(meta ?? null)
            }}
            lat={listingLat}
            lng={listingLng}
            showLocationPicker={!!currentUser?.is_superadmin}
            tiktokConnectionId={
              selectedPlatforms.has("tiktok")
                ? connections.find((c) => c.platform === "tiktok" && c.status === "active")?.id
                : null
            }
            tiktokState={tiktokState}
            onTiktokStateChange={setTiktokState}
          />
        )}

        {/* Schedule toggle */}
        {selectedPlatforms.size > 0 && (
          <div className="space-y-3 border-t border-stone/20 pt-4">
            <div className="flex items-center gap-2">
              <Switch
                id="schedule-toggle"
                checked={scheduleEnabled}
                onCheckedChange={setScheduleEnabled}
              />
              <Label htmlFor="schedule-toggle" className="text-sm">
                Schedule for later
              </Label>
            </div>

            {scheduleEnabled && (
              <DateTimePicker
                value={scheduledAt}
                onChange={setScheduledAt}
                minDate={new Date()}
                placeholder="Choose date & time"
                className="w-auto"
              />
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={
              publishing ||
              selectedPlatforms.size === 0 ||
              (scheduleEnabled && !scheduledAt) ||
              (selectedPlatforms.has("tiktok") && !isTiktokStateValid(tiktokState))
            }
            className="bg-red px-6 text-white hover:bg-red/90"
          >
            {publishing
              ? "Publishing..."
              : scheduleEnabled
                ? "Schedule"
                : `Publish to ${selectedPlatforms.size} platform${selectedPlatforms.size !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>

        </>)}
      </DialogContent>
    </Dialog>
  )
}

function NoConnectionsCallout() {
  return (
    <div className="rounded-lg border border-stone/30 bg-stone/5 px-4 py-5 text-center">
      <svg className="mx-auto mb-2 size-8 text-warm-grey/40" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
      </svg>
      <p className="text-sm font-medium text-charcoal">No social accounts connected</p>
      <p className="mt-1 text-xs text-warm-grey">
        Connect your social media accounts to start publishing.
      </p>
      <a
        href="/dashboard/connections"
        className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-deep-red"
      >
        Connect accounts
        <svg className="size-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
        </svg>
      </a>
    </div>
  )
}
